import type { FC } from "react";
import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import type { Theme } from "@mui/material";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  IconButton,
  useMediaQuery,
  Drawer,
  Link,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { RouterLink } from "src/components/router-link";
import { usePathname } from "src/hooks/use-pathname";
import { useWindowScroll } from "src/hooks/use-window-scroll";
import { paths } from "src/paths";

const TOP_NAV_HEIGHT = 68;

interface NavItem {
  title: string;
  sectionId?: string;
  path?: string;
}

const navItems: NavItem[] = [
  { title: "Services",     sectionId: "services" },
  { title: "OTC Desk",     sectionId: "how-it-works" },
  { title: "How It Works", sectionId: "how-it-works" },
  { title: "Contact",      sectionId: "contact" },
];

function scrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

interface TopNavProps {
  onMobileNavOpen?: () => void;
}

export const TopNav: FC<TopNavProps> = () => {
  const pathname = usePathname();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = pathname === "/";

  useWindowScroll({
    handler: useCallback(() => setScrolled(window.scrollY > 60), []),
    delay: 80,
  });

  return (
    <>
      {/* ── Header ── */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          zIndex: (t) => t.zIndex.appBar,
          transition: "all 0.3s ease",
          ...(scrolled
            ? {
                background: "rgba(8, 15, 30, 0.92)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderBottom: "1px solid rgba(59, 130, 246, 0.12)",
                boxShadow: "0 4px 30px rgba(0,0,0,0.4)",
              }
            : {
                background: "transparent",
              }),
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            sx={{ height: TOP_NAV_HEIGHT }}
          >
            {/* Logo */}
            <Box
              component={RouterLink}
              href={paths.index}
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src="/assets/swift-ramp-logo.png"
                alt="Platinx Exchange"
                sx={{ height: 48, width: "auto", objectFit: "contain" }}
              />
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Desktop Nav */}
            {mdUp && (
              <Stack
                component="nav"
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ mr: 3 }}
              >
                {navItems.map((item) => (
                  <Box
                    key={item.title}
                    component="button"
                    onClick={() => item.sectionId && scrollTo(item.sectionId)}
                    sx={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      px: 2, py: 1,
                      borderRadius: 1.5,
                      color: "rgba(255,255,255,0.75)",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      letterSpacing: "0.01em",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        color: "#60A5FA",
                        background: "rgba(59, 130, 246, 0.1)",
                      },
                    }}
                  >
                    {item.title}
                  </Box>
                ))}
              </Stack>
            )}

            {/* Login CTA */}
            {mdUp && (
              <Button
                component="a"
                href="/dashboard"
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.1,
                  fontSize: "0.875rem",
                  borderRadius: 2,
                  letterSpacing: "0.02em",
                  boxShadow: "0 4px 16px rgba(37, 99, 235, 0.4)",
                  border: "1px solid rgba(96, 165, 250, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)",
                    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.55)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.25s ease",
                }}
              >
                Login
              </Button>
            )}

            {/* Mobile hamburger */}
            {!mdUp && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{ color: "rgba(255,255,255,0.85)", ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Stack>
        </Container>
      </Box>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            background: "#0C1629",
            borderLeft: "1px solid rgba(59, 130, 246, 0.15)",
          },
        }}
      >
        {/* Drawer header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2.5, borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <Box
            component="img"
            src="/assets/swift-ramp-logo.png"
            alt="Platinx Exchange"
            sx={{ height: 38, width: "auto", objectFit: "contain" }}
          />
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{ color: "rgba(255,255,255,0.6)" }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Nav links */}
        <Stack spacing={0.5} sx={{ p: 2.5 }}>
          {navItems.map((item) => (
            <Box
              key={item.title}
              component="button"
              onClick={() => {
                item.sectionId && scrollTo(item.sectionId);
                setMobileOpen(false);
              }}
              sx={{
                background: "none",
                border: "none",
                cursor: "pointer",
                width: "100%",
                textAlign: "left",
                px: 2, py: 1.4,
                borderRadius: 2,
                color: "rgba(255,255,255,0.7)",
                fontSize: "0.95rem",
                fontWeight: 500,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#60A5FA",
                  background: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              {item.title}
            </Box>
          ))}
        </Stack>

        {/* Login button */}
        <Box sx={{ px: 2.5, mt: 2 }}>
          <Button
            component="a"
            href="/dashboard"
            fullWidth
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)",
              color: "#fff",
              fontWeight: 700,
              py: 1.4,
              borderRadius: 2,
              fontSize: "0.95rem",
              boxShadow: "0 4px 16px rgba(37, 99, 235, 0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)",
              },
            }}
          >
            Login to Dashboard
          </Button>
        </Box>

        {/* Contact quick links */}
        <Box sx={{ px: 2.5, mt: "auto", pb: 4, pt: 4, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>
            Contact
          </Typography>
          <Link href="tel:+919560107106" sx={{ display: "block", color: "#60A5FA", fontSize: "0.875rem", mb: 0.75, textDecoration: "none", "&:hover": { color: "#93C5FD" } }}>
            +91 9560107106
          </Link>
          <Link href="mailto:support@platinx.exchange" sx={{ display: "block", color: "#60A5FA", fontSize: "0.875rem", textDecoration: "none", "&:hover": { color: "#93C5FD" } }}>
            support@platinx.exchange
          </Link>
        </Box>
      </Drawer>
    </>
  );
};

TopNav.propTypes = {
  onMobileNavOpen: PropTypes.func,
};
